import React from 'react';
import Modal from '@mui/material/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { RichText } from '@sitecore-jss/sitecore-jss-nextjs';
import {
  StModalContainerWithModal,
  StmodalContainer,
  StHeadingContainer,
  StDescriptionText,
  StFooterbuttons,
  StSubHeadingText,
} from './CustomModal.styles';
import { UILink } from '../../shared/Links/UILink.styles';
import { UIButton } from '../../shared/UIButton.styles';
import { RootState } from 'src/store/store';
import Image from 'next/image';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import { IButtons } from '../NotificationBanner/NotificationBanner';
import { hideModal } from '../../../features/modalSlice';
import { MODAL_VARIANT } from '../../ContactInfo/constants';
import SiteLocationRichText from '../../SiteLocationRichText/SiteLocationRichText';
import CloseIcon from '../../../../public/close-icon.png';
import UiHeading from '../../../components/shared/Heading';

const CustomModal = (): JSX.Element | null => {
  const modalContent = useSelector((state: RootState) => state.modal);
  const dispatch = useDispatch();
  const onClose = () => {
    dispatch(hideModal());
    if (modalContent?.cancelButtonCallback) {
      modalContent.cancelButtonCallback();
    }
  };

  const handleRedirect = () => {
    dispatch(hideModal());
  };

  const onContinueHandler = () => {
    dispatch(hideModal());
    if (modalContent?.continueButtonCallback) {
      modalContent.continueButtonCallback();
    }
  };

  const renderModal =
    modalContent.isOpen && modalContent.title && modalContent.description ? (
      <Modal
        open={modalContent.isOpen}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <StModalContainerWithModal>
          <StmodalContainer>
            <StHeadingContainer>
              <UiHeading id="modal-modal-title" size="h6">
                {modalContent?.title?.value}
              </UiHeading>
              {!modalContent.hideCloseIcon && (
                <IconButton
                  sx={{ top: '-4px' }}
                  color="inherit"
                  aria-label="close modal button"
                  edge="start"
                  onClick={onClose}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') onClose();
                  }}
                  onKeyDown={(e) => {
                    if (e.code === 'Space') onClose();
                  }}
                  data-testid="exitform-disclaimer-image"
                >
                  <Image alt="close-icon" src={CloseIcon} width={12} height={12} />
                </IconButton>
              )}
            </StHeadingContainer>

            <Grid>
              {modalContent.summary && (
                <StSubHeadingText>{modalContent.summary.value}</StSubHeadingText>
              )}
              <Grid className="merge-tag">
                <SiteLocationRichText
                  siteAddressFormat={{ value: modalContent?.siteAddressFormatinModalPopUp?.value }}
                  siteInfo={modalContent.mergeTagData}
                />
              </Grid>
              <StDescriptionText id="modal-modal-description">
                <RichText data-testid="content" field={modalContent?.description} />
              </StDescriptionText>
            </Grid>

            <StFooterbuttons>
              {modalContent.buttons && (
                <>
                  {modalContent.buttons.map((button: IButtons, key: number) => {
                    if (
                      button.url !== '' &&
                      button.variant === '' &&
                      modalContent.variant === MODAL_VARIANT.EXIT_DISCLAIMER
                    ) {
                      return (
                        <UIButton
                          className="btn"
                          key={key}
                          data-testid="exitform-disclaimer-stay-btn"
                          tabIndex={key}
                          aria-label={button.name}
                          href={button.url}
                          onClick={() => onClose()}
                          onKeyUp={(e) => {
                            if (e.key === 'Enter') onClose();
                          }}
                          onKeyDown={(e) => {
                            if (e.code === 'Space') onClose();
                          }}
                        >
                          {button.name}
                        </UIButton>
                      );
                    } else if (
                      button.url === '' &&
                      button.variant !== '' &&
                      modalContent.variant === MODAL_VARIANT.EXIT_DISCLAIMER
                    ) {
                      return (
                        <UILink
                          key={key}
                          onClick={handleRedirect}
                          tabIndex={key}
                          variant={button.variant}
                          target="_blank"
                        >
                          {button.name}
                        </UILink>
                      );
                    } else if (button.url !== '') {
                      return (
                        <UILink
                          key={key}
                          href={button.url}
                          onClick={handleRedirect}
                          tabIndex={key}
                          variant={button.variant}
                          target="_blank"
                        >
                          {button.name}
                        </UILink>
                      );
                    } else {
                      return (
                        <UIButton
                          className="btn"
                          key={key}
                          data-testid="exitform-disclaimer-stay-btn"
                          tabIndex={key}
                          aria-label={button.name}
                          onClick={() => onClose()}
                          onKeyUp={(e) => {
                            if (e.key === 'Enter') onClose();
                          }}
                          onKeyDown={(e) => {
                            if (e.code === 'Space') onClose();
                          }}
                        >
                          {button.name}
                        </UIButton>
                      );
                    }
                  })}
                </>
              )}
              {modalContent.customButtons && modalContent.customButtons.length === 1 && (
                <>
                  <Stack direction="row" spacing={2}>
                    <Button
                      style={{
                        textAlign: 'center',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: '600',
                        lineHeight: 'normal',
                        display: 'flex',
                        padding: '12px 22px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '10px',
                        color: 'white',
                        backgroundColor: '#015FF1',
                        borderRadius: '50px',
                        textTransform: 'capitalize',
                      }}
                      onClick={onContinueHandler}
                    >
                      {modalContent?.customButtons[0]?.fields?.link?.value?.text ||
                        modalContent?.customButtons[0]?.name}
                    </Button>
                  </Stack>
                </>
              )}
              {modalContent.customButtons && modalContent.customButtons.length > 1 && (
                <>
                  <Stack direction="row" spacing={2}>
                    <Link
                      sx={{
                        textAlign: 'center',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: '600',
                        lineHeight: 'normal',
                        display: 'flex',
                        padding: '12px 22px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '10px',
                        textTransform: 'capitalize',
                      }}
                      href="#"
                      underline="none"
                      onClick={onClose}
                    >
                      {modalContent?.customButtons[0]?.fields?.link?.value?.text ||
                        modalContent?.customButtons[0]?.name}
                    </Link>
                    <Button
                      style={{
                        textAlign: 'center',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: '600',
                        lineHeight: 'normal',
                        display: 'flex',
                        padding: '12px 22px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '10px',
                        color: 'white',
                        backgroundColor: '#015FF1',
                        borderRadius: '50px',
                        textTransform: 'capitalize',
                      }}
                      onClick={onContinueHandler}
                    >
                      {modalContent?.customButtons[1]?.fields?.link?.value?.text ||
                        modalContent?.customButtons[1]?.name}
                    </Button>
                  </Stack>
                </>
              )}
            </StFooterbuttons>
          </StmodalContainer>
        </StModalContainerWithModal>
      </Modal>
    ) : null;

  return renderModal;
};

export default CustomModal;
